import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Instagram, Facebook } from 'lucide-react';
import './Footer.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Default contact info
const DEFAULT_CONTACT = {
  company_name: 'Nessenius Garten & Landschaftsbau',
  address: 'Alte Straße 17',
  zip_city: '37176 Nörten-Hardenberg',
  phone: '+49 176 55669090',
  email: 'nesseniusgartenservice@gmail.com',
  opening_hours: {
    mo_fr: '08:00 - 18:00',
    sa: '09:00 - 14:00',
    so: 'Geschlossen'
  }
};

const Footer = () => {
  const [contactInfo, setContactInfo] = useState(DEFAULT_CONTACT);

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/site-content/contact_info`);
        if (response.ok) {
          const data = await response.json();
          if (data.content) {
            setContactInfo(data.content);
          }
        }
      } catch (error) {
        console.error('Error fetching contact info:', error);
      }
    };
    fetchContactInfo();
  }, []);

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-section">
            <h3 className="footer-title">Nessenius Garten & Landschaftsbau</h3>
            <p className="body-small footer-local-text">
              Wir arbeiten jede Woche in Nörten-Hardenberg,<br />
              Göttingen und Umgebung.<br />
              Rufen Sie einfach an.
            </p>
          </div>
          
          <div className="footer-section">
            <h4 className="footer-subtitle">Kontakt</h4>
            <div className="footer-contact">
              <a href={`tel:${contactInfo.phone}`} className="footer-link">
                <Phone size={18} />
                <span>{contactInfo.phone}</span>
              </a>
              <a href={`mailto:${contactInfo.email}`} className="footer-link">
                <Mail size={18} />
                <span>{contactInfo.email}</span>
              </a>
              <div className="footer-link">
                <MapPin size={18} />
                <span>
                  {contactInfo.address}<br />
                  {contactInfo.zip_city}
                </span>
              </div>
            </div>
            <div className="footer-social">
              <a 
                href="https://instagram.com/nesseniusgalabau" 
                target="_blank" 
                rel="noopener noreferrer"
                className="footer-social-btn"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a 
                href="https://www.facebook.com/people/Nessenius-Gartenservice/61578896037050/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="footer-social-btn"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
            </div>
          </div>
          
          <div className="footer-section">
            <h4 className="footer-subtitle">Öffnungszeiten</h4>
            <div className="footer-hours">
              <div className="hours-row">
                <span>Mo - Fr:</span>
                <span>{contactInfo.opening_hours?.mo_fr || '08:00 - 18:00'}</span>
              </div>
              <div className="hours-row">
                <span>Sa:</span>
                <span>{contactInfo.opening_hours?.sa || '09:00 - 14:00'}</span>
              </div>
              <div className="hours-row">
                <span>So:</span>
                <span>{contactInfo.opening_hours?.so || 'Geschlossen'}</span>
              </div>
            </div>
          </div>
          
          <div className="footer-section">
            <h4 className="footer-subtitle">Navigation</h4>
            <div className="footer-links">
              <Link to="/leistungen" className="footer-nav-link">Leistungen</Link>
              <Link to="/projekte" className="footer-nav-link">Projekte</Link>
              <Link to="/ueber-uns" className="footer-nav-link">Über uns</Link>
              <Link to="/bewertungen" className="footer-nav-link">Bewertungen</Link>
              <Link to="/kontakt" className="footer-nav-link">Kontakt</Link>
              <Link to="/servicegebiet" className="footer-nav-link">Servicegebiet</Link>
            </div>
          </div>
        </div>

        {/* SEO Location Links - sitewide internal linking */}
        <div className="footer-seo-links">
          <div className="footer-seo-row">
            <Link to="/gartenpflege/goettingen">Gartenpflege Göttingen</Link>
            <Link to="/erdarbeiten/goettingen">Erdarbeiten Göttingen</Link>
            <Link to="/baumfaellung/goettingen">Baumfällung Göttingen</Link>
            <Link to="/gartenpflege/northeim">Gartenpflege Northeim</Link>
            <Link to="/erdarbeiten/northeim">Erdarbeiten Northeim</Link>
            <Link to="/gartenpflege/duderstadt">Gartenpflege Duderstadt</Link>
            <Link to="/erdarbeiten/einbeck">Erdarbeiten Einbeck</Link>
            <Link to="/gartenpflege/bovenden">Gartenpflege Bovenden</Link>
            <Link to="/baumfaellung/northeim">Baumfällung Northeim</Link>
            <Link to="/hochdruckreinigung/goettingen">Hochdruckreinigung Göttingen</Link>
            <Link to="/erdarbeiten/duderstadt">Erdarbeiten Duderstadt</Link>
            <Link to="/gartenpflege/hardegsen">Gartenpflege Hardegsen</Link>
          </div>
        </div>
        
        <div className="footer-bottom">
          <div className="footer-legal">
            <Link to="/impressum" className="footer-legal-link">Impressum</Link>
            <span className="footer-separator">•</span>
            <Link to="/datenschutz" className="footer-legal-link">Datenschutz</Link>
          </div>
          <div className="footer-copyright">
            © {new Date().getFullYear()} <Link to="/admin" className="footer-admin-link">Nessenius Garten & Landschaftsbau</Link>. Alle Rechte vorbehalten.
            <span className="footer-formerly">(ehemals Nessenius Gartenservice)</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
